import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import AppButton from "../../design-system/components/AppButton";
import { AppStatusChip } from "../../design-system/components/AppChip";
import AppTable from "../../design-system/components/AppTable";
import AppTablePane from "../../design-system/components/AppTablePane";
import AppText from "../../design-system/components/AppText";
import AppTooltip from "../../design-system/components/AppTooltip";
import {
  IconAvailability,
  IconEdit,
  IconError,
  IconHoliday,
  IconSuccess,
  IconSwitch,
  IconTime,
  IconWarning,
} from "../../design-system/icons";
import "./approval-requests-table.css";

const CATEGORY_META = {
  all: { label: "Alle", icon: IconWarning },
  shift_exchange: { label: "Schichttausch", icon: IconSwitch },
  punch_clock: { label: "Stempelung", icon: IconTime },
  absence: { label: "Abwesenheit", icon: IconAvailability },
  holiday: { label: "Ferien", icon: IconHoliday },
  other: { label: "Weitere", icon: IconWarning },
};

const STATUS_META = {
  pending: { label: "Offen", status: "warning" },
  approved: { label: "Genehmigt", status: "success" },
  rejected: { label: "Abgelehnt", status: "error" },
  open: { label: "Offen", status: "warning" },
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "-";
  return new Intl.DateTimeFormat("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const getApprovalCategoryMeta = (category) =>
  CATEGORY_META[category] || CATEGORY_META.other;

const getApprovalStatusMeta = (status) =>
  STATUS_META[String(status || "").toLowerCase()] || STATUS_META.pending;

/*
  Auswaehlbar ist nur, worueber diese Person auch entscheiden darf: bei
  mehrstufigen Anfragen ist zwischendurch jemand anderes am Zug (can_decide ===
  false). Ein Haken auf so einer Zeile waere ein Versprechen, das die
  Sammelgenehmigung nicht halten kann.
*/
export const isApprovalRowSelectable = (row) => row?.can_decide !== false;

const ApprovalRequestsTable = ({
  rows,
  emptyState = "Momentan gibt es keine offenen Genehmigungen.",
  onApprove,
  onReject,
  // Optional: Stempelungen korrigieren/löschen (nur Zeilen der Kategorie punch_clock).
  onEdit,
  decisionId = "",
  paneClassName = "",
  density = "default",
  showStatus = true,
  selectedIds,
  onSelectedIdsChange,
}) => {
  const { t } = useTranslation();
  const isCompact = density === "compact";
  const bodyVariant = density === "compact" ? "body-2" : "body-1";
  const supportingVariant = density === "compact" ? "body-3" : "body-2";
  const selectable = typeof onSelectedIdsChange === "function";
  // Ohne Entscheidungs-Handler (eigene Anfragen der Mitarbeitenden) nur lesen.
  const decidable = typeof onApprove === "function" || typeof onReject === "function";
  const columns = useMemo(() => [
    ...(selectable ? [{
      key: "selected",
      label: "",
      controlKind: "selection",
      sortable: false,
    }] : []),
    {
      key: "title",
      label: "Anfrage",
      ...(isCompact ? { minWidth: 160, grow: 1 } : { minWidth: 210, grow: 1.2 }),
      render: (value, row) => {
        const meta = getApprovalCategoryMeta(row.category);
        const Icon = meta.icon;
        return (
          <div className="approval-request-cell">
            <span className="approval-request-cell__icon" aria-hidden="true"><Icon /></span>
            <span className="approval-request-cell__copy">
              <AppText variant={bodyVariant} truncate title={value || meta.label}>
                {value || meta.label}
              </AppText>
            </span>
          </div>
        );
      },
    },
    {
      key: "requester_name",
      label: "Mitarbeitende",
      minWidth: isCompact ? 150 : 170,
      grow: 1,
    },
    {
      key: "period",
      label: "Zeitraum",
      ...(isCompact ? { minWidth: 180, grow: 1.1 } : { minWidth: 220, grow: 1.2 }),
      render: (value, row) => (
        <span className="approval-period-cell">
          <AppText variant={bodyVariant}>{value || "-"}</AppText>
          {row.category === "punch_clock" && row.break_minutes != null ? (
            <AppText variant={supportingVariant} tone="secondary">
              Pause: {row.break_minutes} Min.
            </AppText>
          ) : null}
        </span>
      ),
    },
    {
      key: "comment",
      label: "Kommentar",
      ...(isCompact ? { minWidth: 160, grow: 1.3 } : { minWidth: 220, grow: 1.2 }),
      render: (value) => {
        const comment = value || "-";
        return <AppText variant={bodyVariant} truncate title={comment}>{comment}</AppText>;
      },
    },
    {
      key: "created_at",
      label: "Erstellt",
      minWidth: isCompact ? 150 : 165,
      grow: isCompact ? 0.9 : 0.8,
      render: (value) => formatDateTime(value),
    },
    ...(showStatus ? [{
      key: "status",
      label: "Status",
      width: 130,
      grow: 0,
      align: "center",
      render: (value) => {
        const meta = getApprovalStatusMeta(value);
        return <AppStatusChip status={meta.status} size="sm">{meta.label}</AppStatusChip>;
      },
    }] : []),
    ...(decidable ? [{
      key: "actions",
      label: "",
      width: typeof onEdit === "function" ? 152 : 112,
      grow: 0,
      align: "center",
      controlKind: "action",
      sortable: false,
      render: (_value, row) => (
        <div className="approval-table-actions">
          {typeof onEdit === "function" ? (
            row.category === "punch_clock" && row.target_id ? (
              <AppTooltip content="Stempelung bearbeiten">
                <AppButton
                  variant="neutral"
                  size="sm"
                  iconOnly
                  aria-label="Stempelung bearbeiten"
                  disabled={decisionId === row.id}
                  onClick={() => onEdit(row)}
                >
                  <IconEdit />
                </AppButton>
              </AppTooltip>
            ) : (
              // Platzhalter: die Knöpfe stehen in jeder Zeile an derselben Stelle.
              <span className="approval-table-actions__spacer" aria-hidden="true" />
            )
          ) : null}
          <AppTooltip content={t("button.approve")}>
            <AppButton
              variant="primary"
              size="sm"
              iconOnly
              aria-label={t("button.approve")}
              disabled={!row.can_decide || decisionId === row.id}
              onClick={() => onApprove(row)}
            >
              <IconSuccess />
            </AppButton>
          </AppTooltip>
          <AppTooltip content={t("button.reject")}>
            <AppButton
              variant="error"
              size="sm"
              iconOnly
              aria-label={t("button.reject")}
              disabled={!row.can_decide || decisionId === row.id}
              onClick={() => onReject(row)}
            >
              <IconError />
            </AppButton>
          </AppTooltip>
        </div>
      ),
    }] : []),
  ], [bodyVariant, decidable, decisionId, isCompact, onApprove, onEdit, onReject, selectable, showStatus, supportingVariant, t]);

  return (
    <AppTablePane className={paneClassName}>
      <AppTable
        className="approval-table"
        rows={rows}
        columns={columns}
        totalRowCount={rows.length}
        getRowId={(row) => row.id}
        emptyState={emptyState}
        density={density}
        {...(selectable ? {
          selectedRowIds: selectedIds || [],
          onSelectedRowIdsChange: onSelectedIdsChange,
          isRowSelectable: isApprovalRowSelectable,
        } : {})}
      />
    </AppTablePane>
  );
};

export default ApprovalRequestsTable;
